/**
 * Rollout flags for the consumer feed filters (Stage C of the
 * consumer-feed-filters spec — `galactus-dev-workspace/docs/agents/handoffs/
 * consumer-feed-filters/handoff.md` @ `Danielle-design/consumer-feed-filters`).
 *
 * Everything the filter path does is behind ONE env flag, default OFF, so the
 * merged code is inert until Dan flips it per lane: g1 `origin main`
 * auto-deploys user-visible staging, and the legacy path must stay
 * bit-for-bit today's behaviour (bare `{op:'subscribe', channel:'markets'}` +
 * explicit snapshot, no fencing, no acks) until then.
 *
 *   VITE_FEED_FILTERS          `on` | `1` | `true` ⇒ send the versioned
 *                              `filter` object on every markets subscribe
 *                              (class/type narrowing + browser cadence),
 *                              install the ack'd fencing pair, drop stale
 *                              frames, and clear out-of-scope rows on every
 *                              filterReset. Anything else ⇒ legacy.
 *   VITE_FEED_FILTERS_FIREHOSE `on` ⇒ ALSO send the filter on the FIREHOSE
 *                              connection. Off by default because the
 *                              firehose is OG hydra on every lane today, and
 *                              OG does not speak Stage A: its subscribe schema
 *                              strips unknown keys (hydra ConsumerProtocol.ts,
 *                              zod default), so it would silently take a bare
 *                              subscribe, send no ack and no stamps — and the
 *                              fence would admit NOTHING. The firehose stays
 *                              legacy until it is a core (or a bench harness).
 *   VITE_FEED_FILTERS_CORES    the filter on CORE fleet feeds (hc1…) — the
 *                              WARM-STANDBY TIER: every sport, class trims,
 *                              cadence 1000 while an edge owns what is on
 *                              screen. ON by default with the flag (Dan,
 *                              2026-09-02: hc1 runs Stage B and carries every
 *                              sport). `off` keeps cores legacy — do that on
 *                              any lane whose core predates Stage B (it
 *                              would strip the object, never ack, and its
 *                              sports would go dark).
 *   VITE_FEED_FILTERS_SPORTS   Phase 2: the `sports` allow-list from the
 *                              mounted views' declarations (the dashboard
 *                              narrows to its selected sport; every other
 *                              route asks for all). ON by default whenever
 *                              VITE_FEED_FILTERS is on — Dan, 2026-09-02, on
 *                              the g1.pm smoke ("I'm on NFL tab, look at that
 *                              Live Feed": wall-to-wall tennis from he1),
 *                              reversing the 2026-06-12 toggle-latency ruling:
 *                              "filtering for only what's visible on a page is
 *                              a good enough first start". `off` disables the
 *                              axis (class narrowing stays). The 30 s narrowing
 *                              grace keeps recently viewed sports warm, so a
 *                              quick chip flip back is still a local re-filter.
 *
 *   VITE_FEED_FILTERS_TICKERS  Tickers axis (feed-filters-per-view, 2026-09-12):
 *                              when every mounted source names its rows
 *                              (Position Tracker, trader / org detail, the
 *                              trades ledger, the account plane) the edge
 *                              gets the exact allow-list — ≤ 1,024 tickers
 *                              is exempt from the governor's cadence floor.
 *                              Edges only, like `games`. ON by default with
 *                              the flag; `off` keeps sport/game narrowing.
 *   VITE_FEED_FILTERS_BOARD    Board line scoping (g1xl-v2#1053 Part 3): the
 *                              dashboard names the rows it draws — each
 *                              game's moneyline pair plus the spread / total
 *                              strikes nearest even money — on the tickers
 *                              axis, from a light REST index on the owning
 *                              edge, instead of pulling the whole alternate
 *                              ladder (Poly: 54 spreads per CFB game). Needs
 *                              the tickers axis. streamrift: OFF unless
 *                              explicitly `on` (main defaults it ON) — node1
 *                              has no edge serving the line index, and the
 *                              gateway carries the board; adopted inert.
 *   VITE_FEED_FILTERS_ODDS     The versioned filter on the `oddspapi` channel
 *                              (odds-gamestate-feed-filters, Stage C′): a
 *                              board receives only the OP families it draws
 *                              (full-game moneyline; full-game spread + total
 *                              on the Spread/Total board) and only a Game
 *                              Detail pulls its game whole — sent on EVERY
 *                              OP-capable ladder core, the standby included.
 *                              Needs VITE_FEED_FILTERS. OFF by default: an
 *                              engine without Stage B′ costs an 8 s ack wait
 *                              and a reconnect per socket (spec D1a), so it
 *                              stays off until every ladder core runs it.
 *   VITE_FEED_FILTERS_GAMESTATE The versioned filter on the Core `gamestate`
 *                              socket: `{games: <the readers' keys>}` instead
 *                              of every record. Needs VITE_FEED_FILTERS. OFF
 *                              by default, same reason.
 *
 * RUNTIME OVERRIDE (per browser, no rebuild — the staging smoke + Stage D
 * A/B on ONE deploy): every flag can be forced `on`/`off` from the URL once
 * (`?feedFilters=on`, `?feedFiltersCores=on`, `?feedFiltersFirehose=on`,
 * `?feedFiltersSports=on`, `?feedFiltersGames=`, `?feedFiltersTickers=`, `?feedFiltersBoard=`,
 * `?feedFiltersOdds=`, `?feedFiltersGamestate=`), which persists to localStorage
 * (`g1xl.feedFilters` …) and applies from the next page load; `=env` clears
 * the override. The override wins over the env in both directions. Read at
 * fleet construction (page load) — the SharedWorker latches the mode per
 * worker name, so a change needs a reload (and, for a shared worker, the
 * other tabs of that feed reload too).
 *
 * `?directoryFollow=off` rides the same fold, though it is not a filter: it
 * restores the one-fetch directory and named-primary-only pair ownership
 * (see `directoryFollowEnabled`).
 *
 * Read at call time (not module load) so unit tests can `vi.stubEnv` both.
 */

function envOn(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'on' || v === '1' || v === 'true';
}
function envOff(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'off' || v === '0' || v === 'false';
}

const OVERRIDE_KEYS = {
  filters: 'g1xl.feedFilters',
  cores: 'g1xl.feedFiltersCores',
  firehose: 'g1xl.feedFiltersFirehose',
  sports: 'g1xl.feedFiltersSports',
  games: 'g1xl.feedFiltersGames',
  gateway: 'g1xl.feedGateway',
  tickers: 'g1xl.feedFiltersTickers',
  boardViewport: 'g1xl.boardViewportStream',
  board: 'g1xl.feedFiltersBoard',
  fleetContinuity: 'g1xl.fleetSourceContinuity',
  odds: 'g1xl.feedFiltersOdds',
  gamestate: 'g1xl.feedFiltersGamestate',
  follow: 'g1xl.directoryFollow',
} as const;
const QUERY_KEYS: Record<keyof typeof OVERRIDE_KEYS, string> = {
  filters: 'feedFilters', cores: 'feedFiltersCores', firehose: 'feedFiltersFirehose', sports: 'feedFiltersSports',
  games: 'feedFiltersGames', tickers: 'feedFiltersTickers', board: 'feedFiltersBoard',
  gateway: 'feedGateway',
  boardViewport: 'boardViewportStream',
  fleetContinuity: 'fleetSourceContinuity',
  odds: 'feedFiltersOdds', gamestate: 'feedFiltersGamestate', follow: 'directoryFollow',
};

/** Fold `?feedFilters…=on|off|env` URL params into localStorage. Idempotent;
 *  safe without window/storage (tests, workers). Returns true if any key was
 *  written, so a caller can strip the params if it wants to. */
export function applyFeedFilterUrlOverrides(search?: string): boolean {
  if (typeof window === 'undefined') return false;
  let wrote = false;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    for (const k of Object.keys(OVERRIDE_KEYS) as Array<keyof typeof OVERRIDE_KEYS>) {
      const v = params.get(QUERY_KEYS[k])?.trim().toLowerCase();
      if (v == null) continue;
      if (v === 'env' || v === '') localStorage.removeItem(OVERRIDE_KEYS[k]);
      else localStorage.setItem(OVERRIDE_KEYS[k], envOn(v) ? 'on' : 'off');
      wrote = true;
    }
  } catch { /* storage unavailable — env decides */ }
  return wrote;
}

/** null = no override (env decides); true/false = forced. */
function override(k: keyof typeof OVERRIDE_KEYS): boolean | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(OVERRIDE_KEYS[k]);
    return v == null ? null : v === 'on';
  } catch {
    return null;
  }
}

function flag(k: keyof typeof OVERRIDE_KEYS, envRaw: string | undefined): boolean {
  return override(k) ?? envOn(envRaw);
}
/** Default-ON variant: only an explicit `off` (override or env) disables. */
function flagDefaultOn(k: keyof typeof OVERRIDE_KEYS, envRaw: string | undefined): boolean {
  const o = override(k);
  return o != null ? o : !envOff(envRaw);
}

/**
 * Feed gateway (2026-09-21): ONE socket per tab to the server-side gateway,
 * which sends only the rows the tab's scopes match — instead of one socket per
 * engine with the fan-in done here. EXCLUSIVE with the fleet: when this is on
 * the fleet, the live/book lanes and the SharedWorker never start (two
 * transports ingesting the same rows would flap row provenance). Default OFF;
 * `?feedGateway=on` or `localStorage['g1xl.feedGateway']='on'` opts one browser in.
 */
export function feedGatewayEnabled(): boolean {
  return flag('gateway', import.meta.env.VITE_FEED_GATEWAY as string | undefined);
}

/**
 * Board viewport streaming (2026-09-24, gateway only): the gateway HOLDS every
 * board row (`FEEDGW_BOARD_HOLD_ALL` on the server) and the Spread/Total board
 * names the rows it draws — the games in view (selected rung ±1, both venues)
 * and the expanded game's whole ladder — through `stores/rowNeeds`. A trader's
 * CFB lines tab was still taking 160–760 Kalshi frames/s after the Polymarket
 * hold; the rows in view are a few dozen. Default OFF; `?boardViewportStream=on`
 * or `localStorage['g1xl.boardViewportStream']='on'` opts one browser in.
 * Meaningless without the gateway (the fleet path has no row hold).
 */
export function boardViewportStreamEnabled(): boolean {
  return feedGatewayEnabled() && flag('boardViewport', import.meta.env.VITE_BOARD_VIEWPORT_STREAM as string | undefined);
}

/**
 * Fleet source continuity (node1, 2026-10-09): whether a DIRECT fleet client
 * forwards the engines' #847 `sourceContinuity` controls to the store, which
 * promotes that feed into the evidence gate on its first control. The gateway
 * never forwards them, so its rows stay on age-based freshness; the fleet is
 * only the gateway's fallback here, and the SuperHydra fork edges began
 * sending controls on 10-09 ~21:35Z. Default OFF keeps a fallen-back tab on
 * the same freshness as the gateway; `?fleetSourceContinuity=on` or
 * `VITE_FLEET_SOURCE_CONTINUITY=on` opts in once the lane is ready for it.
 */
export function fleetSourceContinuityEnabled(): boolean {
  return flag('fleetContinuity', import.meta.env.VITE_FLEET_SOURCE_CONTINUITY as string | undefined);
}

/** Gateway socket URL. Same-origin `/feedws` unless the env names another. */
export function feedGatewayUrl(): string {
  const env = (import.meta.env.VITE_FEED_GATEWAY_URL as string | undefined)?.trim();
  if (env) return env;
  if (typeof window === 'undefined') return '';
  return `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/feedws`;
}

/** Phase 1: class/type narrowing + cadence + fencing (edges). */
export function feedFiltersEnabled(): boolean {
  return flag('filters', import.meta.env.VITE_FEED_FILTERS as string | undefined);
}

/** Phase 2: per-sport narrowing. Requires Phase 1; ON unless explicitly off. */
export function feedFilterSportsEnabled(): boolean {
  return feedFiltersEnabled() && flagDefaultOn('sports', import.meta.env.VITE_FEED_FILTERS_SPORTS as string | undefined);
}

/** Phase 3: per-EVENT narrowing (the games axis — hydracore e37e33d,
 *  hydraedge c93b18e). Requires Phase 2, because a game is a narrowing WITHIN
 *  a sport and the surfaces that declare a game declare its sport too.
 *
 *  ON with the flag, like the sports axis (hydra 2026-09-04 closed the
 *  declaration with option 1). The keys a page declares come from
 *  `marketStore.getGameWireKeys` — the wire values its OWN rows carry — so a
 *  key the engines cannot match is unconstructible here. Before a page's rows
 *  land it has no keys and declares its sport alone, so the axis simply does
 *  not ride yet; it never rides empty. Per-browser kill switch:
 *  `?feedFiltersGames=off`. */
export function feedFilterGamesEnabled(): boolean {
  return feedFilterSportsEnabled() && flagDefaultOn('games', import.meta.env.VITE_FEED_FILTERS_GAMES as string | undefined);
}

/** Tickers axis (feed-filters-per-view, 2026-09-12): a connection whose
 *  every mounted source names its rows (the Position Tracker, a trader /
 *  org detail, the account plane) sends the exact allow-list instead of a
 *  sport. Edges only, like `games`. Requires Phase 2 (the fold a ticker-
 *  scoped source carries for a broad sibling is a sports/games fold). ON
 *  with the flag; per-browser kill switch `?feedFiltersTickers=off`. */
export function feedFilterTickersEnabled(): boolean {
  return feedFilterSportsEnabled() && flagDefaultOn('tickers', import.meta.env.VITE_FEED_FILTERS_TICKERS as string | undefined);
}

/** Board line scoping (g1xl-v2#1053 Part 3) — rides the tickers axis.
 *  streamrift keeps it OFF unless explicitly turned on (`VITE_FEED_FILTERS_BOARD=on`
 *  or `?feedFiltersBoard=on`): main's default-on needs the owning edge's
 *  line index, which node1's fork edges do not serve (Josh, 261007 merge). */
export function feedFilterBoardEnabled(): boolean {
  return feedFilterTickersEnabled() && flag('board', import.meta.env.VITE_FEED_FILTERS_BOARD as string | undefined);
}

/** The `oddspapi` filter (odds-gamestate-feed-filters D6). Requires the
 *  master flag — `?feedFilters=off` turns it off with everything else —
 *  and is OFF unless asked for (env or `?feedFiltersOdds=on`). */
export function feedFilterOddsEnabled(): boolean {
  return feedFiltersEnabled() && flag('odds', import.meta.env.VITE_FEED_FILTERS_ODDS as string | undefined);
}

/** The Core `gamestate` filter (D6). Same rule as the odds flag. */
export function feedFilterGameStateEnabled(): boolean {
  return feedFiltersEnabled() && flag('gamestate', import.meta.env.VITE_FEED_FILTERS_GAMESTATE as string | undefined);
}

/** The firehose (OG today) speaks the filter only when explicitly told so. */
export function feedFilterFirehoseEnabled(): boolean {
  return feedFiltersEnabled() && flag('firehose', import.meta.env.VITE_FEED_FILTERS_FIREHOSE as string | undefined);
}

/** Directory follow (directory-follow, 2026-10-14): the directory is
 *  re-fetched on announce / connection triggers and every 60 s, and an
 *  assigned pair whose named primary stops announcing goes to the sole other
 *  claimant announcing it. ON unless `?directoryFollow=off`, which restores
 *  the one fetch per page load and named-primary-only ownership. No env var:
 *  a per-browser switch for diagnosis, not a lane setting. */
export function directoryFollowEnabled(): boolean {
  return flagDefaultOn('follow', undefined);
}

/** Cores speak the filter (the warm-standby tier) unless explicitly off. */
export function feedFilterCoresEnabled(): boolean {
  return feedFiltersEnabled() && flagDefaultOn('cores', import.meta.env.VITE_FEED_FILTERS_CORES as string | undefined);
}

/** Resolved once per client construction and shipped to the SharedWorker on
 *  `__init` (the worker is transport-agnostic and never reads env). */
export interface FeedFilterMode {
  enabled: boolean;
  sportsAxis: boolean;
  /** Games axis (one event). Edges only: the core floor is the background for
   *  every game, so narrowing it to one event would strand every held
   *  position's mark. */
  gamesAxis?: boolean;
  /** Tickers axis (exact rows). Edges only, same reasoning as `games`: the
   *  core floor is the background for every held position. */
  tickersAxis?: boolean;
  /** Core floor scoping (g1xl-v2#1053 Part 2e): the floor's sports axis is
   *  the viewed union ∪ the held rows' sports (`floorSports`) when the union
   *  is narrow — every held position's mark still rides the floor, but the
   *  floor no longer carries every sport at 1 s for a tab that looks at
   *  one. Cores only; omitted on a broad union (the floor of before).
   *  #1106: an ALT key an edge is serving, and that the union hands to that
   *  edge, is left out (a cold standby); on a broad union the floor then
   *  sends the explicit list of every sport g1 renders less that key. That
   *  is runtime POLICY (`setMarketFilterPolicy({ floorOmitSports })`), not
   *  a mode flag. */
  floorAxis?: boolean;
  /** Requested markets cadence for this connection. Browsers 250; the core
   *  floor 1000 while it is the warm standby (re-negotiated at runtime by
   *  the fleet — see useHydra floorCadence). */
  cadenceMs?: number;
  /** The `oddspapi` filter on this connection (odds-gamestate-feed-filters
   *  D6). Independent of `enabled` (the markets filter): it rides only on an
   *  OP-capable feed — every ladder core, the standby and a dedicated OP
   *  transport included — and the client ignores it elsewhere. */
  opFilter?: boolean;
}

export function resolveFeedFilterMode(): FeedFilterMode {
  return { enabled: feedFiltersEnabled(), sportsAxis: feedFilterSportsEnabled(), gamesAxis: feedFilterGamesEnabled(), tickersAxis: feedFilterTickersEnabled() };
}

/** The browser default the engines assume when a filter names no cadence. */
export const LIVE_LANE_DEFAULT_CADENCE_MS = 250;

/**
 * Market-channel cadence for the /live + /sniper per-sport lane
 * (`liveGameLane`), from `VITE_LIVE_LANE_CADENCE_MS`. Default 250 = today's
 * behaviour. Positive integers only; anything else falls back to 250.
 *
 * What the dial actually reaches (F3, 2026-09-09):
 *  - It rides the versioned `filter` object, so it is INERT unless
 *    VITE_FEED_FILTERS is on for the lane's connection.
 *  - An EDGE honours a requested cadence down to its own coalesce floor
 *    (HYDRA_API.md "cadenceMs" — edge-requestable). Book frames are never
 *    coalesced by cadence; this is the market-SCALAR stream only.
 *  - The D10 FIREHOSE pins 250 ms regardless of the request (OG does not
 *    speak Stage A; a core floor negotiates its own 1000 ms standby cadence
 *    in useHydra). So a lane that dials the firehose candidate sees no
 *    change — verify the lane landed on an edge (`laneCandidatesFor`) before
 *    reading a 25 ms trial as "no effect".
 *  - node1 trial value: 25.
 */
export function liveLaneCadenceMs(): number {
  const raw = (import.meta.env.VITE_LIVE_LANE_CADENCE_MS as string | undefined)?.trim();
  if (!raw) return LIVE_LANE_DEFAULT_CADENCE_MS;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : LIVE_LANE_DEFAULT_CADENCE_MS;
}

/** The live lane's mode: the env-resolved rollout flags plus its own cadence. */
export function feedFilterModeForLiveLane(): FeedFilterMode {
  return { ...resolveFeedFilterMode(), cadenceMs: liveLaneCadenceMs() };
}

/** Per-feed mode: edges follow VITE_FEED_FILTERS; cores additionally need
 *  VITE_FEED_FILTERS_CORES and the firehose VITE_FEED_FILTERS_FIREHOSE (see
 *  above) — one switch per engine family, matching how the families deploy.
 *  A dedicated OP transport never sends a markets filter (it subscribes no
 *  markets at all — HydraClient short-circuits on opOnly regardless). */
export function feedFilterModeFor(role: 'firehose' | 'core' | 'edge'): FeedFilterMode {
  const enabled = role === 'firehose' ? feedFilterFirehoseEnabled()
    : role === 'core' ? feedFilterCoresEnabled()
    : feedFiltersEnabled();
  // The two-tier model: edges narrow to what is on screen; the core floor
  // never narrows by sport (it is the background for everything) and runs
  // at the standby cadence until the fleet says otherwise.
  // Core floor: no per-view axes — but, with Phase 2 on, the floor carries
  // the viewed union ∪ held sports instead of every sport (Part 2e).
  // The odds filter rides only when on, so the flag-off mode (and the
  // `__init` it ships to the worker) is exactly what it was.
  if (role === 'core') return { enabled, sportsAxis: false, gamesAxis: false, tickersAxis: false, floorAxis: enabled && feedFilterSportsEnabled(), cadenceMs: 1000, ...(feedFilterOddsEnabled() ? { opFilter: true } : {}) };
  return {
    enabled,
    sportsAxis: enabled && feedFilterSportsEnabled(),
    // EDGES ONLY (Danielle R16 DNL-014): the firehose was inheriting this from
    // the shared branch, contrary to the contract the doc comment states.
    //
    // The reason is the one stated at the top of this file, NOT a fallback
    // argument: OG does not speak Stage A. Its subscribe schema strips unknown
    // keys, so a `games` allow-list sent there is silently discarded and the
    // connection keeps delivering everything while this client believes it
    // narrowed — the worst shape of wrong, since nothing surfaces the gap.
    // (Dan, 2026-09-04, correcting an earlier draft of this comment: OG is NOT
    // a fallback surface. It carries essentially only Unabated now and is being
    // retired; hydracore is the WebSocket fallback and the REST primary. The
    // connection that must never narrow is therefore the CORE floor, which is
    // carved out above.) Only an edge, which speaks the grammar and whose sport
    // is already on screen, may carry this axis.
    gamesAxis: role === 'edge' && enabled && feedFilterGamesEnabled(),
    // Same carve-out: an exact allow-list on the floor would strand every
    // held position that is NOT on the current page.
    tickersAxis: role === 'edge' && enabled && feedFilterTickersEnabled(),
    cadenceMs: 250,
  };
}
